import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MapTooltipMachineContext } from '~/contexts/MapTooltipMachineContext'
import { useIsTouchDevice } from '~/hooks/useIsTouchDevice'
import { useAppStore } from '~/hooks'
import { Tooltip } from 'react-tooltip'
import {
  GRAPHS,
  type GraphId,
  PMTileSources,
  WAVE_UNITS,
  wavePropertyLabels,
  WAVES_OPTIONS,
  DEPTHS,
  COSMOS_SITE_ADAPTATION_CODES,
  cosmosSiteForBuildingSource,
  type AdaptationCode,
} from '~/constants'
import { Text } from '~/components/typography'
import { kFormatter } from '~/util'
import { formatCategoriesForDisplay } from '~/util/buildingTypes'
import { adaptationDamageComparison, buildingDamagesForScenario } from '~/util/buildingDamages'
import { AdaptationIcon } from '~/components/icons/AdaptationIcon'
import { track } from '~/analytics'

function AdaptationTooltipRow({
  adaptationType,
  label,
}: {
  adaptationType: string
  label: string
}) {
  return (
    <div className="flex flex-shrink-0 items-center gap-2">
      <AdaptationIcon adaptation={adaptationType} className="h-5 w-5" />
      <Text level={4}>{label}</Text>
    </div>
  )
}

const WAVES_PROPERTY_TO_I18N: Record<string, string> = {
  hs_rp_100: 'waves.hsRp100',
  hs_rp_50: 'waves.hsRp50',
  Hs12: 'waves.hs12',
  DirMEF: 'waves.meanEnergyFluxDirection',
  extremes_energy_flux_averaged_direction: 'waves.extremeEnergyFluxDirection',
  WPsum: 'waves.totalWavePower',
  Hsmean_mean_percentage_change: 'waves.changeInMeanHs',
  Hs_q99_mean_percentage_change: 'waves.changeInHs99thPctl',
  hs_rp_100_mean_percentage_change: 'waves.changeInHsRp100',
  WPmean_mean_percentage_change: 'waves.changeInMeanWavePower',
  hs_change: 'waves.projectedHsChangeByRp',
}

export function MapTooltip() {
  const { t } = useTranslation()
  const isTouchDevice = useIsTouchDevice()
  const { context, value } = MapTooltipMachineContext.useSelector((s) => s)
  const {
    wavesProperty,
    dataset,
    selectedGraphType,
    setSelectedGraphType,
    mapRef,
    setOverlayPanelVisible,
    dynamicWavesProperty,
    DEM,
    returnPeriod,
    adaptation,
    avertedDamagesEnabled,
  } = useAppStore()

  const waveFeature = useMemo(() => {
    if (mapRef && context.wavePointName) {
      const sourceLayers = DEPTHS.map((d) => `depth_${d}`)
      const features = sourceLayers.flatMap(
        (sourceLayer) =>
          mapRef.querySourceFeatures(PMTileSources.WAVES, {
            filter: ['==', 'point_name', context.wavePointName],
            sourceLayer,
          }) ?? []
      )
      const f = features.find((f) => f.properties.point_name === context.wavePointName)
      return f
    }
    // Theoretically, we are running this hook *every* time a new tile loads
    // when there is a wave point chosen. This *could* result in bad
    // performance if the user has a point open and is panning around a lot.
    // It is possible to reduce this further by only running once upon load,
    // but there's an annoying edge-case where user A sends user B a deeplink
    // which does *not* include the point they are linking to inside of their
    // viewport. We could probably optimize this further by bailing early on
    // checks when our viewport does not contain any wave points.
    // TODO: Remove mapRef?.areTilesLoaded?.() from the dependency array and
    // instead listen to map 'sourcedata' or 'idle' events to trigger
    // re-computation. Calling a method in a dep array is a React anti-pattern
  }, [mapRef, context?.wavePointName, dataset, mapRef?.style ? mapRef.areTilesLoaded() : false])

  const pointName = waveFeature?.properties.point_name

  const buildingTooltipContent = useMemo(() => {
    if (dataset !== 'cosmos') return null
    const dem = DEM ?? ''
    const rp = returnPeriod ?? ''
    const adapt = adaptation ?? '000'
    const site = context.buildingSource
      ? cosmosSiteForBuildingSource(context.buildingSource)
      : undefined
    const siteSupportsAdaptation =
      site != null &&
      adapt !== '000' &&
      COSMOS_SITE_ADAPTATION_CODES[site].includes(adapt as AdaptationCode)
    const comparison =
      dem && rp && siteSupportsAdaptation && avertedDamagesEnabled
        ? adaptationDamageComparison(context.buildingProperties, dem, rp, adapt, {
            // Match BuildingLayer coalesce: missing adapted columns = fully protected (0).
            missingAdaptedAsZero: true,
          })
        : null
    const showComparison = comparison != null
    // Adapted mode: absolute damages for the scenario the map is drawing.
    const totalSuffix = `dem${dem}_rp${rp}_adapt${siteSupportsAdaptation ? adapt : '000'}`
    const totalDamages = showComparison
      ? null
      : buildingDamagesForScenario(context.buildingProperties, totalSuffix)
    const hasBuilding =
      (context.buildingCategories?.length ?? 0) > 0 ||
      context.buildingOcctype != null ||
      context.buildingProperties != null ||
      totalDamages != null ||
      showComparison
    if (!hasBuilding) return null
    const categories = context.buildingCategories ?? []
    const title =
      categories.length > 0 ? formatCategoriesForDisplay(categories, t) : t('common.unknown')
    const formatCost = (value: number | null) => {
      if (value == null) return null
      const toFixed = Math.abs(value) >= 1_000_000 ? 2 : 0
      const formatted = kFormatter(value, undefined, toFixed)
      return formatted ? `$${Array.isArray(formatted) ? formatted.join('') : formatted}` : null
    }
    const roundPercent = (value: number) =>
      Math.abs(value) >= 10 ? Math.round(value) : Math.round(value * 10) / 10
    const formatPercent = (value: number) => {
      const rounded = roundPercent(value)
      const sign = rounded > 0 ? '+' : ''
      return `${sign}${rounded}%`
    }
    const formatPercentMagnitude = (value: number) => `${Math.abs(roundPercent(value))}%`

    if (showComparison && comparison) {
      if (comparison.direction === 'increased') {
        const additionalCost = formatCost(-comparison.delta)
        const increase =
          comparison.percentVsBaseline != null
            ? t('tooltips.percentIncrease', {
                percent: formatPercentMagnitude(comparison.percentVsBaseline),
              })
            : null
        return {
          title,
          mode: 'comparison' as const,
          avertedFormatted: null,
          remainingFormatted: null,
          relativeLabel: null,
          additionalFormatted:
            additionalCost != null && increase != null
              ? `${additionalCost} ${increase}`
              : additionalCost,
          totalFormatted: formatCost(comparison.adapted),
        }
      }

      if (comparison.direction === 'unchanged') {
        return {
          title,
          mode: 'comparison' as const,
          avertedFormatted: null,
          remainingFormatted: null,
          relativeLabel: t('tooltips.noDamagesAverted'),
          additionalFormatted: null,
          totalFormatted: formatCost(comparison.adapted),
        }
      }

      const fullyAverted = comparison.adapted <= 0
      const avertedCost = formatCost(comparison.delta)
      const reduction =
        !fullyAverted && comparison.percentVsBaseline != null
          ? t('tooltips.percentReduction', {
              percent: formatPercent(comparison.percentVsBaseline),
            })
          : null

      return {
        title,
        mode: 'comparison' as const,
        avertedFormatted:
          avertedCost != null && reduction != null ? `${avertedCost} ${reduction}` : avertedCost,
        remainingFormatted: fullyAverted ? null : formatCost(comparison.adapted),
        relativeLabel: fullyAverted ? t('tooltips.fullyProtected') : null,
        additionalFormatted: null,
        totalFormatted: null,
      }
    }

    return {
      title,
      mode: 'total' as const,
      damagesFormatted: formatCost(totalDamages),
    }
  }, [
    dataset,
    DEM,
    returnPeriod,
    adaptation,
    avertedDamagesEnabled,
    context.buildingCategories,
    context.buildingOcctype,
    context.buildingProperties,
    context.buildingSource,
    t,
  ])

  // Reopen overlay panel only when a point is clicked (not on hover) if a graph type is selected.
  useEffect(() => {
    if (dataset === 'waves' && selectedGraphType && pointName && value === 'visible_from_click') {
      setOverlayPanelVisible(true)
    }
  }, [pointName, selectedGraphType, dataset, setOverlayPanelVisible, value])

  const isVisible = ['visible_from_hover', 'visible_from_click'].includes(String(value))
  const showAdaptationTooltip = isVisible && dataset === 'cosmos' && context.adaptationType != null
  // Hide per-building damage details; keep color-coding and other map tooltips.
  const showBuildingTooltip = false
  const showFloodDepthTooltip =
    isVisible &&
    dataset === 'cosmos' &&
    !showAdaptationTooltip &&
    !showBuildingTooltip &&
    context.floodDepthLabel != null
  const showWaveTooltip = isVisible && dataset === 'waves' && waveFeature != null

  if (
    !isVisible ||
    (!showAdaptationTooltip && !showBuildingTooltip && !showFloodDepthTooltip && !showWaveTooltip)
  ) {
    return null
  }

  const rawWavesProperty = dynamicWavesProperty || wavesProperty
  const wavesPropertyToUse = WAVES_OPTIONS.some((opt) => opt.value === rawWavesProperty)
    ? rawWavesProperty
    : (WAVES_OPTIONS[0]?.value ?? rawWavesProperty)
  const propertyValue = waveFeature?.properties?.[wavesPropertyToUse ?? '']
  const unit = wavesPropertyToUse
    ? WAVE_UNITS[wavesPropertyToUse as keyof typeof WAVE_UNITS]
    : undefined
  const propertyLabel =
    wavesPropertyToUse && WAVES_PROPERTY_TO_I18N[wavesPropertyToUse]
      ? t(WAVES_PROPERTY_TO_I18N[wavesPropertyToUse])
      : wavesPropertyToUse
        ? wavePropertyLabels[wavesPropertyToUse] || wavesPropertyToUse
        : ''
  const displayValue =
    propertyValue !== undefined && propertyValue !== null
      ? unit
        ? kFormatter(propertyValue, unit, 2)
        : propertyValue
      : t('common.na')

  const handleGraphSelection = (graphId: GraphId, isSelected: boolean) => {
    setSelectedGraphType(isSelected ? null : graphId)
    track('wave_graph_selected', {
      graph: graphId,
      action: isSelected ? 'closed' : 'opened',
    })
  }

  return (
    <>
      <div
        className="absolute z-40 bg-neutral-off-white"
        data-tooltip-id={`map-tooltip`}
        style={{
          left: context.screenPosition?.x ?? 0,
          top: context.screenPosition?.y ?? 0,
        }}
      />
      <Tooltip
        id={`map-tooltip`}
        place="top"
        opacity={1.0}
        delayShow={isTouchDevice ? 0 : 500}
        style={{ backgroundColor: '#EDEDED', color: '#66717C', borderRadius: '0.75rem' }}
        className="z-40 flex flex-col gap-y-2 rounded-xl p-3 shadow-sm"
        isOpen
        clickable={true}
      >
        {showAdaptationTooltip && context.adaptationType ? (
          <AdaptationTooltipRow
            adaptationType={context.adaptationType}
            label={t(`adaptation.${context.adaptationType}`)}
          />
        ) : showBuildingTooltip && buildingTooltipContent ? (
          <div className="flex flex-col gap-y-2 text-left">
            <Text level={1} className="font-semibold">
              {buildingTooltipContent.title}
            </Text>
            {buildingTooltipContent.mode === 'comparison' ? (
              <div className="flex flex-col gap-y-0.5">
                {buildingTooltipContent.avertedFormatted != null && (
                  <Text level={3} as="span">
                    {t('tooltips.avertedDamages')}: {buildingTooltipContent.avertedFormatted}
                  </Text>
                )}
                {buildingTooltipContent.additionalFormatted != null && (
                  <Text level={3} as="span">
                    {t('tooltips.additionalDamages')}: {buildingTooltipContent.additionalFormatted}
                  </Text>
                )}
                {buildingTooltipContent.remainingFormatted != null && (
                  <Text level={3} as="span">
                    {t('tooltips.remainingDamages')}: {buildingTooltipContent.remainingFormatted}
                  </Text>
                )}
                {buildingTooltipContent.totalFormatted != null && (
                  <Text level={3} as="span">
                    {t('tooltips.totalDamages')}: {buildingTooltipContent.totalFormatted}
                  </Text>
                )}
                {buildingTooltipContent.relativeLabel != null && (
                  <Text level={3} as="span">
                    {buildingTooltipContent.relativeLabel}
                  </Text>
                )}
              </div>
            ) : (
              buildingTooltipContent.damagesFormatted != null && (
                <div className="flex flex-col gap-y-0.5">
                  <Text level={3} as="span">
                    {t('tooltips.totalDamages')}: {buildingTooltipContent.damagesFormatted}
                  </Text>
                </div>
              )
            )}
          </div>
        ) : showFloodDepthTooltip && context.floodDepthLabel ? (
          <div className="flex flex-col gap-y-2 text-left">
            <Text level={1} className="font-semibold">
              {t('tooltips.floodedDepth', { depth: context.floodDepthLabel })}
            </Text>
          </div>
        ) : showWaveTooltip && waveFeature ? (
          <div className="flex flex-col gap-y-2 text-left">
            <Text level={1} className="font-semibold">
              {waveFeature.properties.point_name}
            </Text>
            <Text level={3}>
              {propertyLabel}: {displayValue}
            </Text>
            <div className="flex flex-col">
              <Text level={5}>{t('tooltips.viewMoreData')}</Text>
              <div className="flex flex-wrap justify-center gap-2">
                {Object.entries(GRAPHS).map(([id, { label, Icon }]) => {
                  const isSelected = selectedGraphType === id
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => handleGraphSelection(id as GraphId, isSelected)}
                      className={`flex cursor-pointer items-center justify-center rounded-lg p-2 ${
                        isSelected
                          ? 'bg-neutral-white outline outline-2 outline-neutral-midfi-grey hover:bg-gray-200'
                          : 'hover:bg-gray-200 hover:outline hover:outline-[1px] hover:outline-neutral-midfi-grey/50'
                      }`}
                      data-tooltip-id="graph-button-tooltip"
                      data-tooltip-content={label}
                      aria-label={label}
                      aria-pressed={isSelected}
                    >
                      <Icon className="h-5 w-5" />
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        ) : null}
      </Tooltip>
      <Tooltip
        id="graph-button-tooltip"
        place="bottom"
        delayShow={500}
        style={{ backgroundColor: '#EDEDED', color: '#66717C', borderRadius: '0.5rem' }}
        className="z-50 px-2 py-1 text-xs shadow-sm"
      />
    </>
  )
}
